import { useEffect, useRef } from 'react';

export default function DifferentiatorSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add('opacity-100', 'scale-100'); },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const features = [
    'Seals water pathways within concrete',
    'Performs under hydrostatic pressure',
    'No demolition or shutdowns',
    'Predictable, long-term results — guaranteed',
  ];

  const comparison = [
    {
      bad: 'Surface sealing',
      good: 'Deep concrete penetration',
    },
    {
      bad: 'Polyurethane fails under pressure',
      good: 'Non-expansive polyvinyl',
    },
    {
      bad: 'Creates voids & redirects water',
      good: 'Fills cracks & joints permanently',
    },
    {
      bad: 'No warranty',
      good: '5-year warranty',
    },
  ];

  return (
    <>
      {/* Bold blue differentiator */}
      <section className="bg-[#0055ff] py-14 md:py-20">
        <div
          ref={ref}
          className="max-w-5xl mx-auto px-4 md:px-8 text-center opacity-0 scale-95 transition-all duration-700 ease-out"
        >
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-black text-white mb-5 leading-tight">
            We Don&apos;t Patch Leaks.<br />We Engineer Permanent Solutions.
          </h2>
          <p className="text-white/80 text-base md:text-xl max-w-3xl mx-auto mb-10 leading-relaxed">
            Engineered, non-destructive solutions using{' '}
            <strong className="text-white">Elastic Polyvinyl Liquid DWG Nano-Polymer ECO V®</strong>{' '}
            at <strong className="text-white">4,061 PSI</strong> — for structures where failure is not an option.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 max-w-2xl mx-auto">
            {features.map((f) => (
              <div key={f} className="flex items-center gap-3 bg-white/10 rounded-lg px-4 py-3 text-left">
                <span className="w-5 h-5 flex items-center justify-center shrink-0 text-white">
                  <i className="ri-checkbox-circle-fill text-lg"></i>
                </span>
                <span className="text-white font-semibold text-sm">{f}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="bg-[#060912] py-14 md:py-24">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10 md:mb-12">
            <h2 className="text-xl md:text-3xl font-black text-white mb-3">
              Why Conventional Methods Fail
            </h2>
            <p className="text-gray-400 text-sm md:text-base">
              CLWizard&apos;s method is engineered, controlled, and verifiable.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {/* Conventional */}
            <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-5">
                <span className="w-8 h-8 flex items-center justify-center bg-red-500/20 rounded-full text-red-400">
                  <i className="ri-close-circle-fill text-lg"></i>
                </span>
                <h3 className="text-white font-bold text-lg">Conventional Methods</h3>
              </div>
              <ul className="space-y-3">
                {comparison.map((c) => (
                  <li key={c.bad} className="flex items-start gap-3 text-gray-400 text-sm">
                    <span className="w-4 h-4 flex items-center justify-center shrink-0 text-red-500 mt-0.5">
                      <i className="ri-close-line text-base"></i>
                    </span>
                    {c.bad}
                  </li>
                ))}
              </ul>
            </div>

            {/* CLWizard */}
            <div className="bg-[#0055ff]/5 border border-[#0055ff]/30 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-5">
                <span className="w-8 h-8 flex items-center justify-center bg-[#0055ff]/20 rounded-full text-[#4d8fff]">
                  <i className="ri-checkbox-circle-fill text-lg"></i>
                </span>
                <h3 className="text-white font-bold text-lg">CLWizard® Method</h3>
              </div>
              <ul className="space-y-3">
                {comparison.map((c) => (
                  <li key={c.good} className="flex items-start gap-3 text-gray-300 text-sm">
                    <span className="w-4 h-4 flex items-center justify-center shrink-0 text-[#0055ff] mt-0.5">
                      <i className="ri-check-line text-base"></i>
                    </span>
                    {c.good}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
